import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import "../../components/common/H5PromptModal.css";
import { EasyAvatar } from "fin-web-components";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { resolveAppAvatarUrl } from "../../utils/resolveAppMediaUrl";
import { copyTextToClipboard } from "../../utils/copyTextToClipboard";
import { h5NavigateTo, isLfMiniProgramEnv } from "../../utils/miniProgramEnv";
import type { SpaceProfile } from "../SpaceType";
import { formatSpaceMetricValue } from "../spacePageUtils";
import { SPACE_HOME_AVATAR_CONFIG, SPACE_HOME_LAYOUT } from "./Config";
import { SpaceHomeCertLabel } from "./SpaceHomeCertLabel";
import { WeworkServiceApi } from "../../wework_service/WeworkServiceApi";
import { WeworkServiceModal } from "../../wework_service/WeworkServiceModal";
import type { WeworkServiceCardView } from "../../wework_service/WeworkServiceType";

/** 未关注：较粗白色加号（笔画粗细见 Config.followBtn.plusStrokeWidth） */
function SpaceHomeFollowPlusIcon() {
  return (
    <svg className="space-home-follow-btn__plus" viewBox="0 0 12 12" aria-hidden focusable="false">
      <line x1="6" y1="2.5" x2="6" y2="9.5" />
      <line x1="2.5" y1="6" x2="9.5" y2="6" />
    </svg>
  );
}

/** 抖音式：粉色 Hero（头像+基本信息）+ 白色圆角底栏（内容区） */
export function SpaceHomeInfoSection(props: {
  profile: SpaceProfile;
  isLoggedIn: boolean;
  favorStatus: 0 | 1;
  onLoginRequest: () => void;
  onFollow: () => void;
  onRequestUnfollow: () => void;
  children?: ReactNode;
}) {
  const {
    profile,
    isLoggedIn,
    favorStatus,
    onLoginRequest,
    onFollow,
    onRequestUnfollow,
    children,
  } = props;
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const prevFavorStatusRef = useRef(favorStatus);
  const followFlashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [followCheckVisible, setFollowCheckVisible] = useState(false);
  const [weworkOpen, setWeworkOpen] = useState(false);
  const [weworkLoading, setWeworkLoading] = useState(false);
  const [weworkCard, setWeworkCard] = useState<WeworkServiceCardView | null>(null);
  const certLabel = profile.certLabelName?.trim() ?? "";
  const introText = profile.signature?.trim() ?? "";
  const avatarUrl = resolveAppAvatarUrl(profile.avatarUrl);
  const douyinHomeUrl = profile.douyinHomeUrl?.trim() ?? "";
  const douyinId = profile.douyinId?.trim() ?? "";

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onDocClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [menuOpen]);

  const clearFollowFlashTimer = useCallback(() => {
    if (followFlashTimerRef.current) {
      clearTimeout(followFlashTimerRef.current);
      followFlashTimerRef.current = null;
    }
  }, []);

  const showFollowCheckmark = useCallback(() => {
    clearFollowFlashTimer();
    setFollowCheckVisible(true);
    followFlashTimerRef.current = setTimeout(() => {
      setFollowCheckVisible(false);
      followFlashTimerRef.current = null;
    }, SPACE_HOME_LAYOUT.followBtn.checkmarkDurationMs);
  }, [clearFollowFlashTimer]);

  useEffect(() => {
    const prev = prevFavorStatusRef.current;
    if (prev === 0 && favorStatus === 1) {
      showFollowCheckmark();
    } else if (favorStatus === 0) {
      clearFollowFlashTimer();
      setFollowCheckVisible(false);
    }
    prevFavorStatusRef.current = favorStatus;
  }, [clearFollowFlashTimer, favorStatus, showFollowCheckmark]);

  useEffect(() => {
    return () => {
      clearFollowFlashTimer();
    };
  }, [clearFollowFlashTimer]);

  const showShareCopiedModal = useCallback((spaceName: string) => {
    const modal = Modal.info({
      className: "lf-h5-prompt-modal",
      icon: null,
      centered: true,
      width: 320,
      closable: true,
      maskClosable: true,
      footer: null,
      content: (
        <>
          <div className="lf-h5-prompt-modal__body">
            <p className="lf-h5-prompt-modal__title">
              您已经复制了「{spaceName}」的网址链接，快去分享吧。
            </p>
          </div>
          <div className="lf-h5-prompt-modal__actions lf-h5-prompt-modal__actions--single">
            <button
              type="button"
              className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--primary lf-h5-prompt-modal__btn--block"
              onClick={() => modal.destroy()}
            >
              我知道了
            </button>
          </div>
        </>
      ),
    });
  }, []);

  const showDouyinCopiedModal = useCallback(() => {
    const modal = Modal.info({
      className: "lf-h5-prompt-modal",
      icon: null,
      centered: true,
      width: 320,
      closable: true,
      maskClosable: true,
      footer: null,
      content: (
        <>
          <div className="lf-h5-prompt-modal__body">
            <p className="lf-h5-prompt-modal__title">抖音号复制成功，在抖音中打开即可。</p>
          </div>
          <div className="lf-h5-prompt-modal__actions lf-h5-prompt-modal__actions--single">
            <button
              type="button"
              className="lf-h5-prompt-modal__btn lf-h5-prompt-modal__btn--primary lf-h5-prompt-modal__btn--block"
              onClick={() => modal.destroy()}
            >
              我知道了
            </button>
          </div>
        </>
      ),
    });
  }, []);

  const onCopySpaceId = useCallback(() => {
    void copyTextToClipboard(profile.spaceId).then((ok) => {
      if (ok) {
        message.success("已复制龙粉号");
      } else {
        message.error("复制失败，请重试");
      }
    });
  }, [profile.spaceId]);

  const onGoPlatformHome = useCallback(() => {
    setMenuOpen(false);
    h5NavigateTo(navigate, "/");
  }, [navigate]);

  const onShareHome = useCallback(() => {
    setMenuOpen(false);
    const base = process.env.PUBLIC_URL || "";
    const url = `${window.location.origin}${base}/space/${profile.spaceId}/home`;
    void copyTextToClipboard(url).then((ok) => {
      if (ok) {
        showShareCopiedModal(profile.name);
      } else {
        message.error("复制失败，请重试");
      }
    });
  }, [profile.name, profile.spaceId, showShareCopiedModal]);

  const onAssistantWecom = useCallback(() => {
    setMenuOpen(false);
    if (!isLoggedIn) {
      message.info("请先登录后再联系助理企微");
      onLoginRequest();
      return;
    }
    setWeworkLoading(true);
    void WeworkServiceApi.getMyBound({ spaceId: profile.spaceId })
      .then((res) => {
        if (!res?.card?.qrcodeUrl?.trim()) {
          message.warning("暂未配置空间企业微信客服，请联系空间管理员");
          return;
        }
        setWeworkCard(res.card);
        setWeworkOpen(true);
      })
      .catch((e: unknown) => {
        message.error(e instanceof Error ? e.message : "加载失败");
      })
      .finally(() => {
        setWeworkLoading(false);
      });
  }, [isLoggedIn, onLoginRequest, profile.spaceId]);

  const onGoDouyin = useCallback(() => {
    setMenuOpen(false);
    if (!douyinHomeUrl && !douyinId) {
      message.warning("暂未配置抖音主页链接");
      return;
    }
    if (isLfMiniProgramEnv()) {
      const copyText = douyinHomeUrl || douyinId;
      void copyTextToClipboard(copyText).then((ok) => {
        if (ok) {
          showDouyinCopiedModal();
        } else {
          message.error("复制失败，请重试");
        }
      });
      return;
    }
    if (!douyinHomeUrl) {
      message.warning("暂未配置抖音主页链接");
      return;
    }
    window.open(douyinHomeUrl, "_blank", "noopener,noreferrer");
  }, [douyinHomeUrl, douyinId, showDouyinCopiedModal]);

  const onMenuUnfollow = useCallback(() => {
    setMenuOpen(false);
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    onRequestUnfollow();
  }, [isLoggedIn, onLoginRequest, onRequestUnfollow]);

  const onFollowBtnClick = useCallback(() => {
    if (!isLoggedIn) {
      onLoginRequest();
      return;
    }
    if (favorStatus === 1) {
      return;
    }
    onFollow();
  }, [favorStatus, isLoggedIn, onFollow, onLoginRequest]);

  const showFollowBtn = favorStatus === 0 || followCheckVisible;
  const followBtnMode = followCheckVisible ? "check" : "plus";

  return (
    <>
      <header className="space-home-hero" aria-label="空间基本信息">
        <div className="space-home-top-bar">
          <div className="space-home-top-menu" ref={menuRef}>
            <button
              type="button"
              className="space-home-top-btn space-home-top-btn--menu"
              aria-label="更多操作"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              <span className="space-home-top-menu-icon" aria-hidden />
            </button>
            {menuOpen ? (
              <div className="space-home-top-menu-pop" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="space-home-top-menu-item"
                  onClick={onGoPlatformHome}
                >
                  龙粉社首页
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="space-home-top-menu-item"
                  onClick={onShareHome}
                >
                  分享主页
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="space-home-top-menu-item"
                  disabled={weworkLoading}
                  onClick={onAssistantWecom}
                >
                  助理企微
                </button>
                {douyinHomeUrl ? (
                  <button
                    type="button"
                    role="menuitem"
                    className="space-home-top-menu-item"
                    onClick={onGoDouyin}
                  >
                    前往抖音号
                  </button>
                ) : null}
                {isLoggedIn && favorStatus === 1 ? (
                  <>
                    <div className="space-home-top-menu-divider" aria-hidden />
                    <button
                      type="button"
                      role="menuitem"
                      className="space-home-top-menu-item space-home-top-menu-item--danger"
                      onClick={onMenuUnfollow}
                    >
                      取消关注
                    </button>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="space-home-hero-body">
          <div className="space-home-hero-inner">
            <div className="space-home-avatar-wrap">
              <EasyAvatar
                shape="circle"
                size={SPACE_HOME_AVATAR_CONFIG.size}
                url={avatarUrl || undefined}
                word={avatarDisplayWord(profile.name, profile.spaceId)}
                alt=""
                className="space-home-avatar"
                border={{
                  width: SPACE_HOME_AVATAR_CONFIG.borderWidth,
                  color: SPACE_HOME_AVATAR_CONFIG.borderColor,
                }}
              />
              {showFollowBtn ? (
                <button
                  type="button"
                  className={`space-home-follow-btn space-home-follow-btn--${followBtnMode}`}
                  aria-label={followBtnMode === "check" ? "已关注" : "关注"}
                  onClick={onFollowBtnClick}
                >
                  {followBtnMode === "check" ? (
                    <CheckOutlined className="space-home-follow-btn__icon" aria-hidden />
                  ) : (
                    <SpaceHomeFollowPlusIcon />
                  )}
                </button>
              ) : null}
            </div>
            <div className="space-home-hero-text">
              <div className="space-home-name-row">
                <h1 className="space-home-name">{profile.name}</h1>
                {certLabel ? <SpaceHomeCertLabel name={certLabel} /> : null}
              </div>
              <div className="space-home-id-row">
                <span className="space-home-id-text">龙粉号：{profile.spaceId}</span>
                <button
                  type="button"
                  className="space-home-id-copy"
                  aria-label="复制龙粉号"
                  title="复制"
                  onClick={onCopySpaceId}
                >
                  <CopyOutlined />
                </button>
              </div>
              <div className="space-home-metrics" aria-label="空间数据">
                <div className="space-home-metric">
                  <span className="space-home-metric-value">
                    {formatSpaceMetricValue(profile.followerCount)}
                  </span>
                  <span className="space-home-metric-label">粉丝</span>
                </div>
                <div className="space-home-metric">
                  <span className="space-home-metric-value">
                    {formatSpaceMetricValue(profile.subscriptionCount ?? 0)}
                  </span>
                  <span className="space-home-metric-label">订阅</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="space-home-sheet" aria-label="空间主页">
        <div className="space-home-sheet-body">
          {introText ? <p className="space-home-intro">{introText}</p> : null}
          {children}
        </div>
      </section>
      <WeworkServiceModal
        open={weworkOpen}
        title="助理企微"
        content=""
        card={weworkCard}
        onClose={() => setWeworkOpen(false)}
      />
    </>
  );
}
